import { Grid, Link, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { CreateUserRequestBody, DbUser } from "../../../../interfaces";
import { Role } from "../../../../enums";
import { AlertState } from "../../../../types";
import { ClassSelect } from "../utils/ClassSelect";
import { RoleSelect } from "../utils/RoleSelect";
import { SchoolSelect } from "../utils/SchoolSelect";
import { TeacherSelect } from "../utils/TeacherSelect";
import { UserManagementModal } from "../UserManagementModal";
import {
  UserCreationState,
  getError,
  useUserCreationState,
} from "../state/useUserCreationState";
import { BulkStudentsFileInput, BulkUsersArray } from "../utils/BulkStudentsFileInput";
import axios from "../../../../services/axios";
import { generateGtmEvent } from "../../../../Helpers";

type AddBulkUsersProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
};

export function AddBulkUsers(props: AddBulkUsersProps) {
  const [state, dispatch] = useUserCreationState(props);
  const [users, setUsers] = useState<BulkUsersArray>([]);
  const [csvError, setCsvError] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();

  const updateForm = useCallback(
    (update: Partial<UserCreationState["form"]>) =>
      dispatch({ type: "updateForm", update }),
    [dispatch]
  );

  const handleSubmit = async () => {
    HandleDataLayer(
      "form",
      "click",
      "onclick",
      "click",
      "modal",
      "add bulk users",
      "save"
    );
    const { form } = state;
    let succeeded = 0;
    const erroredUsers: string[] = [];
    await Promise.allSettled(
      users.map(async (user) => {
        try {
          await axios.post<CreateUserRequestBody>("/cognito-users", {
            ...user,
            role: form.role,
            class_ids: [Number(form.classId)],
            school_id: Number(form.schoolId),
          });
          succeeded++;
        } catch (e) {
          erroredUsers.push(user.email);
        }
      })
    );
    const selectedClass = state.data.classes.value.find(
      (item) => item.id === Number(form.classId)
    );
    const selectedSchool = state.data.schools.value.find(
      (item) => item.id === Number(form.schoolId)
    );
    const selectedTeacher = state.data.teachers.value.find(
      (item) => item.id === form.teacherId
    );
    HandleDataLayer(
      "form",
      "adding bulk users",
      "onsubmit",
      "submit",
      "modal",
      "add bulk users",
      "users added",
      {
        role: form.role,
        school: `${selectedSchool ? selectedSchool.name : ""}`,
        class: `${selectedClass ? selectedClass.subject : ""} ${
          selectedClass ? selectedClass.period_block : ""
        }`,
        teacher: `${selectedTeacher ? selectedTeacher.full_name : ""}`,
        file_name: fileName,
      }
    );
    if (erroredUsers.length === 0) {
      props.onSubmit({
        severity: "success",
        message: `${users.length} ${form.role} users created.`,
      });
    } else {
      props.onSubmit({
        severity: "error",
        message: `Some users could not be created: Succeeded: ${succeeded}. Failed: ${
          erroredUsers.length
        }. Failed users: ${erroredUsers.join(", ")}. The user(s) may already exist.`,
        meta: {
          location: "AddBulkUsers",
          error: erroredUsers,
        },
      });
    }
  };

  const handleCsvLoad = async (users: BulkUsersArray, fileName: string) => {
    setCsvError(undefined);
    setFileName(fileName);
    setUsers(users);
  };

  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string,
    metaData?: any
  ) => {
    generateGtmEvent({ event, action, name, type, region, section, text }, metaData);
  };

  return (
    <UserManagementModal
      title="Add Bulk Users"
      open={props.open}
      onClose={props.onClose}
      onSave={handleSubmit}
      errorText={csvError ?? getError(state)}
      disableSubmit={!users.length}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography display="inline" color="textSecondary">
            Teachers and admin users have the ability to add students in bulk to the Digit
            system. For a successful upload, it is recommended to use
          </Typography>{" "}
          <Link
            display="inline"
            href={`${process.env.PUBLIC_URL}/Digit Batch Upload User Template.csv`}>
            this bulk upload csv file template.
          </Link>
        </Grid>
        <Grid item xs={6}>
          <RoleSelect
            value={state.form.role}
            onChange={(role) => {
              updateForm({ role });
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a role",
                "modal",
                "add bulk users ^ role",
                role
              );
            }}
            currentUserRole={props.currentUser.role}
          />
        </Grid>
        <Grid item xs={6}>
          <SchoolSelect
            schoolId={state.form.schoolId}
            onChange={(schoolId) => {
              updateForm({ schoolId });
              let schoolName = state.data.schools.value.find(
                (item) => item.id === Number(schoolId)
              );
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a school",
                "modal",
                "add bulk users ^ school",
                schoolName?.name!
              );
            }}
            options={state.data.schools.value}
            required={state.form.role !== Role.super}
            disabled={
              props.currentUser.role !== Role.super || state.form.role === Role.super
            }
          />
        </Grid>
        <Grid item xs={6}>
          <TeacherSelect
            teacherId={state.form.teacherId}
            onChange={(teacherId) => {
              updateForm({ teacherId });
              let teacherName = state.data.teachers.value.find(
                (item) => item.id === teacherId
              );
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a teacher",
                "modal",
                "add bulk users ^ teacher",
                teacherName?.full_name!
              );
            }}
            disabled={
              props.currentUser.role === Role.teacher ||
              state.form.role !== Role.student ||
              !state.form.schoolId
            }
            required={state.form.role === Role.student}
            options={state.data.teachers.value}
          />
        </Grid>
        <Grid item xs={6}>
          <ClassSelect
            classId={state.form.classId}
            onChange={(classId) => {
              updateForm({ classId });
              let className = state.data.classes.value.find(
                (item) => item.id === Number(classId)
              );
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a class",
                "modal",
                "add bulk users ^ class",
                `${className?.subject!} ${className?.period_block!} ${
                  className?.start_date
                    ? new Date(className.start_date).toLocaleDateString("en-US", {
                        timeZone: "UTC",
                      })
                    : ""
                }`
              );
            }}
            options={state.data.classes.value}
            disabled={
              state.form.role !== Role.student ||
              !state.form.schoolId ||
              !state.form.teacherId
            }
            required={state.form.role === Role.student}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography align="center" component={"p"} variant="h6" color="textSecondary">
            Upload .csv file with the list of users
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <BulkStudentsFileInput onChange={handleCsvLoad} onError={setCsvError} />
        </Grid>
      </Grid>
    </UserManagementModal>
  );
}
